import { getApiBaseUrl } from "./videoService.js";

export interface NotificationItem {
  id: string;
  userId: string;
  actorId?: string | null;
  type: string; // "NEW_UPLOAD" | "COMMENT_REPLY" | "TIP_RECEIVED" | "PINNED_COMMENT"
  title: string;
  message: string;
  targetUrl: string;
  thumbnailUrl?: string | null;
  isRead: boolean;
  createdAt: string;
}

export type NotificationTier = "ALL" | "PERSONALIZED" | "NONE";

const STORAGE_KEY_NOTIFS = "hp_inapp_notifications";
const STORAGE_KEY_TIERS = "hp_channel_notification_tiers";

function getLocalNotifications(): NotificationItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_NOTIFS);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch {}
  return [
    {
      id: "notif_seed_01",
      userId: "usr_current",
      actorId: "creator_helix",
      type: "NEW_UPLOAD",
      title: "HP - Helix Creator",
      message: "Uploaded: Love Tonight (Official Video)",
      targetUrl: "/watch/stream_love_tonight_official",
      thumbnailUrl: "https://videodelivery.net/3200cf5100f94e5ab9e3c0fb4c6e7aad/thumbnails/thumbnail.jpg?time=1s&height=360",
      isRead: false,
      createdAt: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
    },
    {
      id: "notif_seed_02",
      userId: "usr_current",
      actorId: "creator_jovial",
      type: "NEW_UPLOAD",
      title: "Jovial I Music",
      message: "Uploaded: Sweet Margaret (Exclusive Audio)",
      targetUrl: "/watch/stream_sweet_margaret_official",
      thumbnailUrl: "https://videodelivery.net/3200cf5100f94e5ab9e3c0fb4c6e7aad/thumbnails/thumbnail.jpg?time=1s&height=360",
      isRead: true,
      createdAt: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
    },
  ];
}

function saveLocalNotifications(list: NotificationItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY_NOTIFS, JSON.stringify(list));
  } catch {}
}

export async function fetchNotifications(userId?: string): Promise<{ notifications: NotificationItem[]; unreadCount: number }> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/api/v1/notifications${userId ? `?userId=${encodeURIComponent(userId)}` : ""}`;

  try {
    const res = await fetch(url, {
      headers: {
        ...(userId ? { "x-user-id": userId } : {}),
      },
    });
    if (res.ok) {
      const data = await res.json();
      if (data && Array.isArray(data.notifications)) {
        return {
          notifications: data.notifications,
          unreadCount: typeof data.unreadCount === "number" ? data.unreadCount : data.notifications.filter((n: any) => !n.isRead).length,
        };
      }
    }
  } catch {}

  const local = getLocalNotifications();
  const unreadCount = local.filter((n) => !n.isRead).length;
  return { notifications: local, unreadCount };
}

export async function markNotificationAsRead(id: string, userId?: string): Promise<boolean> {
  const baseUrl = getApiBaseUrl();
  try {
    const res = await fetch(`${baseUrl}/api/v1/notifications/${id}/read`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(userId ? { "x-user-id": userId } : {}),
      },
    });
    if (res.ok) {
      // also update local cache
      const list = getLocalNotifications().map((n) => (n.id === id ? { ...n, isRead: true } : n));
      saveLocalNotifications(list);
      return true;
    }
  } catch {}

  const list = getLocalNotifications().map((n) => (n.id === id ? { ...n, isRead: true } : n));
  saveLocalNotifications(list);
  return true;
}

export async function markAllNotificationsAsRead(userId?: string): Promise<boolean> {
  const baseUrl = getApiBaseUrl();
  try {
    const res = await fetch(`${baseUrl}/api/v1/notifications/mark-all-read`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(userId ? { "x-user-id": userId } : {}),
      },
    });
    if (res.ok) {
      const list = getLocalNotifications().map((n) => ({ ...n, isRead: true }));
      saveLocalNotifications(list);
      return true;
    }
  } catch {}

  const list = getLocalNotifications().map((n) => ({ ...n, isRead: true }));
  saveLocalNotifications(list);
  return true;
}

export async function updateSubscriptionTier(
  channelId: string,
  tier: NotificationTier,
  userId?: string
): Promise<{ success: boolean; tier: NotificationTier }> {
  const cleanId = channelId.replace(/^@/, "").toLowerCase();
  const baseUrl = getApiBaseUrl();

  // Persist locally
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TIERS) || "{}";
    const tiers = JSON.parse(raw);
    tiers[cleanId] = tier;
    localStorage.setItem(STORAGE_KEY_TIERS, JSON.stringify(tiers));
  } catch {}

  try {
    const res = await fetch(`${baseUrl}/api/v1/subscriptions/${encodeURIComponent(cleanId)}/tier`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(userId ? { "x-user-id": userId } : {}),
      },
      body: JSON.stringify({ tier }),
    });
    if (res.ok) {
      const data = await res.json();
      return { success: true, tier: data.tier || tier };
    }
  } catch {}

  return { success: true, tier };
}

export function getCachedSubscriptionTier(channelId: string): NotificationTier {
  const cleanId = channelId.replace(/^@/, "").toLowerCase();
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TIERS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed[cleanId]) {
        return parsed[cleanId];
      }
    }
  } catch {}
  return "PERSONALIZED";
}
